class MegaMenuTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('[role="tab"]');
    this.panels = this.querySelectorAll('[role="tabpanel"]');
    this.hoverIntent = null;
    this.bindEvents();
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        if (tab.getAttribute('aria-selected') !== 'true') {
          e.preventDefault();
          this.switchTab(tab);
        }
      });
      tab.addEventListener('keydown', (e) => this.onKeyDown(e));
      tab.addEventListener('mouseenter', () => {
        clearTimeout(this.hoverIntent);
        this.hoverIntent = setTimeout(() => this.switchTab(tab), 120);
      });
      tab.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverIntent);
      });
    });
  }

  switchTab(selectedTab) {
    const currentPanel = this.activePanel || this.querySelector('[role="tabpanel"]:not([hidden])');
    const nextPanelId = selectedTab.getAttribute('aria-controls');
    const nextPanel = this.querySelector('#' + nextPanelId);

    if (currentPanel === nextPanel) return;

    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;
    });

    /* Update "View All" link with fade */
    const viewAllLink = this.querySelector('[data-mega-view-all]');
    if (viewAllLink) {
      viewAllLink.classList.add('mega-menu__view-all-link--fading');
      setTimeout(() => {
        viewAllLink.href = selectedTab.dataset.allUrl || '#';
        const svg = viewAllLink.querySelector('svg');
        viewAllLink.textContent = (selectedTab.dataset.allLabel || '') + ' ';
        if (svg) viewAllLink.appendChild(svg);
        viewAllLink.classList.remove('mega-menu__view-all-link--fading');
        viewAllLink.classList.add('mega-menu__view-all-link--entering');
        setTimeout(() => viewAllLink.classList.remove('mega-menu__view-all-link--entering'), 150);
      }, 100);
    }

    if (currentPanel) {
      currentPanel.classList.add('mega-menu__tab-panel--fading');
      setTimeout(() => {
        currentPanel.hidden = true;
        currentPanel.classList.remove('mega-menu__tab-panel--fading');
        nextPanel.hidden = false;
        void nextPanel.offsetHeight;
        nextPanel.classList.add('mega-menu__tab-panel--entering');
        setTimeout(() => nextPanel.classList.remove('mega-menu__tab-panel--entering'), 150);
      }, 100);
    } else {
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== nextPanelId;
      });
    }
    this.activePanel = nextPanel;
  }

  onKeyDown(event) {
    const tabArray = Array.from(this.tabs);
    const index = tabArray.indexOf(event.currentTarget);
    let newIndex;
    if (event.key === 'ArrowRight') newIndex = (index + 1) % tabArray.length;
    else if (event.key === 'ArrowLeft') newIndex = (index - 1 + tabArray.length) % tabArray.length;
    else return;
    event.preventDefault();
    tabArray[newIndex].focus();
    this.switchTab(tabArray[newIndex]);
  }
}

customElements.define('mega-menu-tabs', MegaMenuTabs);

/* ── Hover-to-open with intent delay ── */
document.addEventListener('DOMContentLoaded', () => {
  if (!window.matchMedia('(hover: hover)').matches) return;

  const OPEN_DELAY = 180;
  const FADE_OUT_MS = 200;
  const SWITCH_DELAY = 120;
  let openIntent = null;
  let switchIntent = null;
  let activeMenu = null;
  let isClosing = false;

  const menus = document.querySelectorAll('.header__inline-menu header-menu');
  if (!menus.length) return;

  /* ── Close cursor element ── */
  const closeCursor = document.createElement('div');
  closeCursor.className = 'mega-menu__close-cursor';
  closeCursor.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
  document.body.appendChild(closeCursor);

  function onMouseMove(e) {
    if (isClosing) return;
    const overMenu = e.target.closest('header-menu') || e.target.closest('.section-header') || e.target.closest('.top-bar-section') || e.target.closest('.utility-nav-section');
    if (overMenu) {
      closeCursor.classList.remove('mega-menu__close-cursor--visible');
    } else {
      closeCursor.style.left = e.clientX + 'px';
      closeCursor.style.top = e.clientY + 'px';
      closeCursor.classList.add('mega-menu__close-cursor--visible');
    }
  }

  function open(menu, animate) {
    const details = menu.querySelector('details.mega-menu');
    if (!details) return;
    isClosing = false;
    details.classList.remove('mega-menu--fade-out');
    if (animate) {
      details.classList.add('mega-menu--fade-in');
      details.addEventListener('animationend', () => details.classList.remove('mega-menu--fade-in'), { once: true });
    }
    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mega-menu--overlay-visible');
    activeMenu = menu;
    document.addEventListener('mousemove', onMouseMove);
  }

  function close(menu, animate) {
    const details = menu.querySelector('details.mega-menu');
    if (!details) return;

    if (animate) {
      isClosing = true;
      closeCursor.classList.remove('mega-menu__close-cursor--visible');
      document.removeEventListener('mousemove', onMouseMove);
      details.classList.add('mega-menu--fade-out');
      document.body.classList.remove('mega-menu--overlay-visible');
      setTimeout(() => {
        details.classList.remove('mega-menu--fade-out');
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        if (activeMenu === menu) activeMenu = null;
        isClosing = false;
      }, FADE_OUT_MS);
    } else {
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      if (activeMenu === menu) {
        activeMenu = null;
        document.removeEventListener('mousemove', onMouseMove);
      }
    }
  }

  /* Close mega panel when hovering over the top bar area */
  document.addEventListener('mega-menu:close', () => {
    if (activeMenu && !isClosing) close(activeMenu, true);
  });

  /* Close mega panel when clicking outside (on overlay or elsewhere) */
  document.addEventListener('click', (e) => {
    if (!activeMenu || isClosing) return;
    const clickedInsideMenu = e.target.closest('header-menu');
    if (!clickedInsideMenu) {
      close(activeMenu, true);
    }
  });

  menus.forEach((menu) => {
    /* Prevent summary click from toggling details — let <a> links navigate */
    const summary = menu.querySelector('details.mega-menu > summary');
    if (summary) {
      summary.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        e.preventDefault();
      });
    }

    menu.addEventListener('mouseenter', () => {
      clearTimeout(openIntent);
      clearTimeout(switchIntent);

      if (activeMenu && activeMenu !== menu) {
        /* Switching between items: delayed to avoid accidental triggers */
        switchIntent = setTimeout(() => {
          close(activeMenu, false);
          open(menu, false);
        }, SWITCH_DELAY);
      } else if (!activeMenu) {
        /* First open: fade in */
        openIntent = setTimeout(() => open(menu, true), OPEN_DELAY);
      }
    });

    menu.addEventListener('mouseleave', () => {
      clearTimeout(openIntent);
      clearTimeout(switchIntent);
    });
  });
});
